/**
 * The one writing surface: a toolbar, a box you type into, and whatever the
 * caller hangs beside the buttons. The Room's composer and the automation
 * editors both mount it, so a host formats words the same way wherever they
 * are writing, and there is one place where that behaviour lives.
 *
 * It is controlled. The words come in as html and every change goes back out,
 * which is what lets an automation step keep its body in its saved config.
 *
 * The host writes with buttons, never with markup, so no stray asterisk or
 * square bracket can reach a reader.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { normUrl } from "../lib/links";

interface RichBoxProps {
  /** The words, as html. */
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  /** Buttons after the divider: attach, images, video, record. */
  tools?: ReactNode;
  /** Rows between the toolbar and the box: a recorder, the caller's insert row. */
  under?: ReactNode;
  ariaLabel?: string;
  /** Filled with a function that drops words in at the cursor. */
  insertRef?: { current: ((text: string) => void) | null };
  /** Filled with a function that runs an edit on the remembered selection. */
  commandRef?: { current: ((cmd: string, arg?: string) => void) | null };
  /** Files dropped on the box or pasted into it. Without this they are ignored. */
  onFiles?: (files: File[]) => void;
  /** Just the box, as a plain message field: no toolbar. Formatting still comes from the keyboard. */
  bare?: boolean;
  /** A class on the box itself. */
  className?: string;
}

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor">
      {children}
    </svg>
  );
}
const IconLink = () => (<Svg><path d="M10 13a5 5 0 007 0l2-2a5 5 0 00-7-7l-1 1" /><path d="M14 11a5 5 0 00-7 0l-2 2a5 5 0 007 7l1-1" /></Svg>);
const IconList = () => (<Svg><circle cx="4.5" cy="7" r="1.3" fill="currentColor" stroke="none" /><circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none" /><circle cx="4.5" cy="17" r="1.3" fill="currentColor" stroke="none" /><path d="M9 7h11M9 12h11M9 17h11" /></Svg>);

/** An empty box still reports markup; treat the empties as nothing written. */
const EMPTY = new Set(["", "<br>", "<p></p>", "<div><br></div>", "<p><br></p>"]);
function isRichEmpty(html: string): boolean {
  return EMPTY.has(html.trim());
}

/** The words inside a written body, for "did they write anything" and a title. */
export function richText(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

/** Words written before the box existed, as paragraphs it can hold. */
export function plainToRich(text: string): string {
  const t = (text ?? "").trim();
  if (!t) return "";
  return t
    .split(/\n{2,}/)
    .map(
      (para) =>
        "<p>" +
        para
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br>") +
        "</p>",
    )
    .join("");
}

export function RichBox({
  value,
  onChange,
  placeholder,
  tools,
  under,
  ariaLabel,
  insertRef,
  commandRef,
  onFiles,
  bare = false,
  className,
}: RichBoxProps) {
  const box = useRef<HTMLDivElement>(null);
  const saved = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkVal, setLinkVal] = useState("");
  const [dragging, setDragging] = useState(false);

  // Only write into the element when the words differ from what is already
  // shown. Assigning every render would put the caret back at the front on
  // each keystroke.
  useEffect(() => {
    const el = box.current;
    if (el && el.innerHTML !== value) el.innerHTML = value;
  }, [value]);

  const emit = () => {
    const el = box.current;
    if (el) onChange(isRichEmpty(el.innerHTML) ? "" : el.innerHTML);
  };

  /** Remember where the cursor was, because clicking a button loses it. */
  const remember = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && box.current?.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      saved.current = sel.getRangeAt(0).cloneRange();
    }
  };
  const restore = () => {
    box.current?.focus();
    const r = saved.current;
    if (r && box.current?.contains(r.commonAncestorContainer)) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(r);
    }
  };

  const run = (cmd: string, arg?: string) => {
    restore();
    document.execCommand(cmd, false, arg);
    remember();
    emit();
  };

  /**
   * Drop words in at the cursor. Done with a range rather than `execCommand`,
   * which is deprecated and which the test DOM does not carry out.
   */
  const putWords = (text: string) => {
    const el = box.current;
    if (!el) return;
    restore();
    const sel = window.getSelection();
    const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
    if (range && el.contains(range.commonAncestorContainer)) {
      range.deleteContents();
      const node = document.createTextNode(text);
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(range);
      saved.current = range.cloneRange();
    } else {
      el.append(text);
    }
    emit();
  };

  // The fill-in menu and the caller's own insert row live outside this box.
  useEffect(() => {
    if (insertRef) insertRef.current = putWords;
    if (commandRef) commandRef.current = run;
    return () => {
      if (insertRef) insertRef.current = null;
      if (commandRef) commandRef.current = null;
    };
  });

  const addLink = () => {
    const url = normUrl(linkVal);
    setLinkOpen(false);
    setLinkVal("");
    if (url) run("createLink", url);
  };

  const hold = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    remember();
  };

  return (
    <>
      {bare ? null : (
      <div className="rc-toolbar top">
        <button type="button" className="rc-tool bold" title="Bold" onMouseDown={hold} onClick={() => run("bold")}>
          B
        </button>
        <button type="button" className="rc-tool italic" title="Italic" onMouseDown={hold} onClick={() => run("italic")}>
          I
        </button>
        <button
          type="button"
          className="rc-tool"
          title="Add link"
          onMouseDown={hold}
          onClick={() => setLinkOpen((v) => !v)}
        >
          <IconLink />
          Link
        </button>
        <button
          type="button"
          className="rc-tool"
          title="Bulleted list"
          onMouseDown={hold}
          onClick={() => run("insertUnorderedList")}
        >
          <IconList />
          List
        </button>
        {tools ? (
          <>
            <span className="rc-div" />
            {tools}
          </>
        ) : null}
      </div>
      )}

      {linkOpen ? (
        <div className="rc-insert">
          <input dir="auto"
            className="input"
            value={linkVal}
            autoFocus
            placeholder="Highlight the words first, then paste the address"
            aria-label="The address to link to"
            onChange={(e) => setLinkVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addLink();
              }
              if (e.key === "Escape") setLinkOpen(false);
            }}
          />
          <button type="button" className="btn" style={{ padding: "9px 15px" }} onClick={addLink}>
            Add
          </button>
          <button
            type="button"
            className="rc-x"
            title="Cancel"
            onClick={() => {
              setLinkOpen(false);
              setLinkVal("");
            }}
          >
            Cancel
          </button>
        </div>
      ) : null}

      {under}

      <div
        ref={box}
        className={[bare ? "rc-editor bare" : "rc-editor", dragging ? "dragover" : "", className ?? ""].filter(Boolean).join(" ")}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel ?? placeholder}
        tabIndex={0}
        data-placeholder={placeholder}
        onInput={emit}
        onKeyUp={remember}
        onMouseUp={remember}
        onBlur={() => {
          remember();
          emit();
        }}
        onPaste={(e) => {
          const files = onFiles ? Array.from(e.clipboardData.files) : [];
          const text = e.clipboardData.getData("text/plain");
          // Ours either way: files go to the caller, and words go in as words,
          // never as whatever markup the clipboard was carrying.
          e.preventDefault();
          remember();
          if (files.length) onFiles?.(files);
          if (text) putWords(text);
        }}
        onDragOver={
          onFiles
            ? (e) => {
                e.preventDefault();
                setDragging(true);
              }
            : undefined
        }
        onDragLeave={onFiles ? () => setDragging(false) : undefined}
        onDrop={
          onFiles
            ? (e) => {
                e.preventDefault();
                setDragging(false);
                const files = Array.from(e.dataTransfer.files);
                if (files.length) onFiles(files);
              }
            : undefined
        }
      />
    </>
  );
}
